import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getProductTypes, headerDetailData, tableDetailData } from '../../../api/knowledgeProductApi';
import { TimeRange } from '../../../models/TimeRangeKnowledgeProductsEnum';
import KnowledgeProductTable from '../KnowledgeProductTable/KnowledgeProductTable';
import SummaryHeaderSingleValue from '../SummaryHeaderSingleValue/SummaryHeaderSingleValue';
import styles from './KnowledgeProductDetail.module.scss';
import {
  getCountries,
  getMaxDate,
  getMinDate,
  multipleFilter,
  setObjectParams,
  setRangeDateByDays,
} from './Util';
import KnowledgeProductFilterBubbles from '../KnowledgeProductFilterBubbles/KnowledgeProductFilterBubbles';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { RegionsAcronymEnum } from '../../../models/RegionsAcronymEnum';
import { KnowledgeProductsYearFilterContext } from '../../../context/KnowledgeProductsYearFilterContext';

const setLinkSelection = (setter, stateValue, selected: boolean) => {
  return setter({
    ...stateValue,
    selected,
  });
};

const customStyle = { minWidth: 120 };
const customValueStyle = { fontSize: 36 };
const customSubtitleStyle = { fontSize: 16, fontWeight: 600, color: '#264377', paddingTop: 10 };
const customLinkStyle = { fontSize: 16, fontWeight: 300, textDecoration: 'underline', color: '#264377' };

const getYearFromAvailableDate = (value): number | null => {
  const date = new Date(value);
  const year = date.getFullYear();
  return Number.isNaN(year) ? null : year;
};

const getAvailableYearsFromData = (data): number[] => {
  const years: number[] = data
    .map(item => getYearFromAvailableDate(item.availableDate))
    .filter((year): year is number => year !== null);
  const uniqueYears = Array.from(new Set<number>(years));
  return uniqueYears.sort((a: number, b: number) => b - a);
};

const filterKnowledgeProductsByYear = (data, selectedYear: number) => {
  if (!selectedYear) return data;
  return data.filter(item => getYearFromAvailableDate(item.availableDate) === selectedYear);
};

const mapProductTypesToOptions = response => {
  const asArray = Array.isArray(response) ? response : response?.collection || [];
  const mapped = asArray
    .map((item, index) => {
      if (typeof item === 'string') {
        return { id: index + 1, label: item };
      }
      const id = Number(item?.id ?? item?.value ?? index + 1);
      const label = (item?.name ?? item?.label ?? item?.description ?? item?.value ?? '').toString().trim();
      return { id, label };
    })
    .filter(item => item.label.length > 0);

  const deduped = mapped.filter((item, index, array) => index === array.findIndex(other => other.label === item.label));
  return deduped.sort((a, b) => a.label.localeCompare(b.label));
};

const filterKnowledgeProductsByProductType = (data, productTypeOptions, selectedProductTypeId: number) => {
  if (!selectedProductTypeId) return data;
  const selectedProductType = productTypeOptions.find(option => option.id === selectedProductTypeId);
  if (!selectedProductType) return data;
  const selectedLabel = selectedProductType.label.toLowerCase().trim();
  return data.filter(item => (item?.productType || '').toString().toLowerCase().trim() === selectedLabel);
};

const buildProductTypeForSearchContext = (selectedProductTypeId: number, availableProductTypes) => {
  if (!selectedProductTypeId) {
    return [{ value: 'all', id: 0, label: 'all' }];
  }

  const selectedProductType = availableProductTypes.find(option => option.id === selectedProductTypeId);
  if (!selectedProductType) {
    return [{ value: 'all', id: 0, label: 'all' }];
  }

  return [{ value: selectedProductType.label, id: selectedProductType.id, label: selectedProductType.label }];
};

export const searchObject = {
  from: new Date().toISOString() || null,
  to: new Date().toISOString() || null,
  componenteHandleDateRanges: true,
  rangeDate: 30,
  productType: [{ value: 'all', id: 0, label: 'all' }],
  filteredCountries: [{ value: 'all', id: 0, label: 'all' }],
  departmentDivision: [{ value: 'all', id: 0, label: 'all' }],
  countries: [{ value: 'all', id: 0, label: 'all' }],
  disabledCountriesSelect: false,
  modalFilterUsed: false,
  firstLoad: true,
  minDate: new Date().toISOString() || null,
  maxDate: new Date().toISOString() || null,
  searchButtonFired: false,
};

export const SearchObjectContext = createContext([{}, () => { }]);

const getFirstAvailableRange = totals => {
  if (totals.totalQuantityLast30Days > 0) {
    return TimeRange.Month_1;
  }

  if (totals.totalQuantityLast180Days > 0) {
    return TimeRange.Month_6;
  }

  if (totals.totalQuantityLast365Days > 0) {
    return TimeRange.Month_12;
  }

  return TimeRange.Everyone;
};

type Props = {
  headerTitle: any;
};
const KnowledgeProductDetail = ({ headerTitle }: Props) => {
  const { t } = useTranslation();
  const { layout, header, table } = styles
  const [last30DaysState, setLast30DaysState] = useState({
    value: 0,
    selected: true,
  });
  const [last6MonthsState, setLast6MonthsState] = useState({
    value: 0,
    selected: false,
  });
  const [last12MonthsState, setLast12MonthsState] = useState({
    value: 0,
    selected: false,
  });
  const [everyoneState, setEveryoneState] = useState({ value: 0, selected: false });
  const [tableData, setTableData] = useState([]);
  const [renderData, setRenderData] = useState([]);
  const [filteredModalUsedData, setFilteredModalUsedData] = useState([]);
  const [rangeDate, setRangeDate] = useState(setRangeDateByDays(TimeRange.Month_1));
  const [searchContext, setSearchContext] = useState(searchObject);
  const autoRangeInitialized = useRef(false);
  const filterIndicatorsCommon = useSelector<any, any>(state => state.filterIndicators.common);
  const { regionId: regionsFilterIndicator } = filterIndicatorsCommon;
  const { portfolioSelectedTab } = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const { regions } = useSelector<any, any>(state => state);
  const {
    selectedYear,
    setSelectedYear,
    setAvailableYears,
    selectedProductTypeId,
    setSelectedProductTypeId,
    availableProductTypes,
    setAvailableProductTypes,
  } = useContext(KnowledgeProductsYearFilterContext);

  const [filterBtnData, setFilterBtnData] = useState<boolean>(false);

  const applySelectedKnowledgeFilters = dataToFilter => {
    const yearFiltered = filterKnowledgeProductsByYear(dataToFilter, selectedYear);
    return filterKnowledgeProductsByProductType(yearFiltered, availableProductTypes, selectedProductTypeId);
  };

  const loadKnowledgeProductTypes = async () => {
    try {
      const productTypesResponse = await getProductTypes();
      const options = mapProductTypesToOptions(productTypesResponse);
      setAvailableProductTypes(options);
      if (selectedProductTypeId && !options.find(option => option.id === selectedProductTypeId)) {
        setSelectedProductTypeId(0);
      }
    } catch (error) {
      setAvailableProductTypes([]);
    }
  };

  const handleGetKnowledgeHeaderDetail = async () => {
    const objectParams = setObjectParams(searchContext);
    const data: any = await Promise.all([headerDetailData(objectParams), tableDetailData(rangeDate)]);
    const years = getAvailableYearsFromData(data[1]);
    setLast30DaysState(prev => ({ ...prev, value: data[0].totalQuantityLast30Days }));
    setLast6MonthsState(prev => ({ ...prev, value: data[0].totalQuantityLast180Days }));
    setLast12MonthsState(prev => ({ ...prev, value: data[0].totalQuantityLast365Days }));
    setEveryoneState(prev => ({ ...prev, value: data[0].totalQuantity }));
    setAvailableYears(years);
    if (selectedYear && !years.includes(selectedYear)) {
      setSelectedYear(0);
    }

    if (!autoRangeInitialized.current) {
      const firstAvailableRange = getFirstAvailableRange(data[0]);
      autoRangeInitialized.current = true;
      if (searchContext.rangeDate !== firstAvailableRange) {
        handleSelectedLink(firstAvailableRange, handleRangeDate, linkStateOptions);
        return;
      }
      localStorage.setItem('knowledgeIndicatorRange', firstAvailableRange.toString());
    }

    setTableData(data[1]);
    const multipleFilterData = multipleFilter(data[1], searchContext);
    setRenderData(applySelectedKnowledgeFilters(multipleFilterData));
  };

  useEffect(() => {
    setIndicatorTitle();
    loadKnowledgeProductTypes();
    setSearchContext({
      ...searchContext,
      from: setRangeDateByDays(TimeRange.Month_1).from,
      to: setRangeDateByDays(TimeRange.Month_1).to,
      rangeDate: TimeRange.Month_1,
    });
  }, []);

  useEffect(() => {
    setIndicatorTitle();
  }, [rangeDate]);

  const updateSearchContextOnrenderDataChange = (min, max) => {
    setSearchContext({
      ...searchContext,
      from: min,
      to: max,
      minDate: min,
      maxDate: max,
      firstLoad: false,
      rangeDate: searchContext.rangeDate,
    });
  };

  useEffect(() => {
    if (
      (searchContext.rangeDate === TimeRange.Everyone && searchContext.componenteHandleDateRanges) ||
      searchContext.firstLoad
    ) {
      const minDate = getMinDate(renderData);
      const maxDate = getMaxDate(renderData);
      updateSearchContextOnrenderDataChange(minDate, maxDate);
    }

  }, [renderData]);

  useEffect(() => {
    handleGetKnowledgeHeaderDetail();
    if (searchContext.firstLoad) {
      setSearchContext({
        ...searchContext,
        from: setRangeDateByDays(searchContext.rangeDate).from,
        to: setRangeDateByDays(searchContext.rangeDate).to,
        rangeDate: searchContext.rangeDate,
        firstLoad: false,
      });
      setCountriesToFilter();
    }
  }, [rangeDate]);

  useEffect(() => {
    handleGetKnowledgeHeaderDetail();
    setCountriesToFilter();
  }, [filterIndicatorsCommon]);

  useEffect(() => {
    setSearchContext(prev => ({
      ...prev,
      productType: buildProductTypeForSearchContext(selectedProductTypeId, availableProductTypes),
      searchButtonFired: !prev.searchButtonFired,
    }));
  }, [selectedProductTypeId, availableProductTypes]);

  useEffect(() => {
    handleGetKnowledgeHeaderDetail();
  }, [searchContext.productType]);

  useEffect(() => {
    const baseData = searchContext.modalFilterUsed ? filteredModalUsedData : multipleFilter(tableData, searchContext);
    setRenderData(applySelectedKnowledgeFilters(baseData));
  }, [selectedYear, selectedProductTypeId]);

  const handleRangeDate = rangeSelected => {
    setFilterBtnData(prev => !prev);
    setRangeDate(setRangeDateByDays(rangeSelected));
    const minDate = getMinDate(renderData);
    const maxDate = getMaxDate(renderData);
    setSearchContext({
      ...searchContext,
      from: setRangeDateByDays(rangeSelected).from !== null ? setRangeDateByDays(rangeSelected).from : minDate,
      to: setRangeDateByDays(rangeSelected).to !== null ? setRangeDateByDays(rangeSelected).to : maxDate,
      rangeDate: rangeSelected,
      searchButtonFired: !searchContext.searchButtonFired,
      componenteHandleDateRanges: true,
    });
  };


  const setCountriesToFilter = () => {
    if (
      portfolioSelectedTab.internalId === '0_global_bid' ||
      (regionsFilterIndicator.length &&
        (portfolioSelectedTab.title === RegionsAcronymEnum.CAN ||
          portfolioSelectedTab.title === RegionsAcronymEnum.CCB ||
          portfolioSelectedTab.title === RegionsAcronymEnum.CID ||
          portfolioSelectedTab.title === RegionsAcronymEnum.CSC ||
          portfolioSelectedTab.title === RegionsAcronymEnum.REG))
    ) {
      const haveRegionsInfilter = regionsFilterIndicator.length ? regionsFilterIndicator.map(r => r.label) : [];
      setSearchContext({
        ...searchContext,
        countries: getCountries(regions, haveRegionsInfilter),
        disabledCountriesSelect: false,
      });
    } else {
      setSearchContext({
        ...searchContext,
        countries: getCountries(regions),
        disabledCountriesSelect: false,
      });
    }
  };

  const setIndicatorTitle = () => {
    headerTitle(true, `${t('general_knowledge_products')}`);
  };

  const linkStateOptions = {
    setLast30DaysState,
    last30DaysState,
    setLast6MonthsState,
    last6MonthsState,
    setLast12MonthsState,
    last12MonthsState,
    setEveryoneState,
    everyoneState,
  };

  return (
    <>
      <div className={layout}>
        <KnowledgeProductFilterBubbles searchContext={searchContext} />
        <div className={header}>
          {renderSummaryHeaderSingleValues(linkStateOptions, handleRangeDate, t)}
        </div>
        <div className={table}>
          <KnowledgeProductTable tableData={renderData} filterBtnData={filterBtnData} />
        </div>
      </div>
    </>
  );
};

export default KnowledgeProductDetail;

const renderSummaryHeaderSingleValues = (linkStateOptions, handleRangeDate, t) => {
  const { last30DaysState, last6MonthsState, last12MonthsState, everyoneState } = linkStateOptions;
  return (
    <>
      {' '}
      <SummaryHeaderSingleValue
        value={last30DaysState.value}
        title={t('last_30_days')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isLink={true}
        isSelected={last30DaysState.selected}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Month_1, handleRangeDate, linkStateOptions);
        }}
      />
      <SummaryHeaderSingleValue
        value={last6MonthsState.value}
        title={t('last_6_months')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isLink={true}
        isSelected={last6MonthsState.selected}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Month_6, handleRangeDate, linkStateOptions);
        }}
      />
      <SummaryHeaderSingleValue
        value={last12MonthsState.value}
        title={t('last_12_months')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isSelected={last12MonthsState.selected}
        isLink={true}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Month_12, handleRangeDate, linkStateOptions);
        }}
      />
      <SummaryHeaderSingleValue
        value={everyoneState.value}
        title={t('more_than_12_months')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isLink={true}
        isSelected={everyoneState.selected}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Everyone, handleRangeDate, linkStateOptions);
        }}
      />
    </>
  );
};

const handleSelectedLink = (
  rangeSelected = TimeRange.Month_1,
  handleRangeDate,
  {
    setLast30DaysState,
    last30DaysState,
    setLast6MonthsState,
    last6MonthsState,
    setLast12MonthsState,
    last12MonthsState,
    setEveryoneState,
    everyoneState,
  },
) => {
  handleRangeDate(rangeSelected);
  localStorage.setItem('knowledgeIndicatorRange', rangeSelected.toString());
  switch (rangeSelected) {
    case TimeRange.Month_1:
      setLinkSelection(setLast30DaysState, last30DaysState, true);
      setLinkSelection(setLast6MonthsState, last6MonthsState, false);
      setLinkSelection(setLast12MonthsState, last12MonthsState, false);
      setLinkSelection(setEveryoneState, everyoneState, false);
      break;
    case TimeRange.Month_6:
      setLinkSelection(setLast30DaysState, last30DaysState, false);
      setLinkSelection(setLast6MonthsState, last6MonthsState, true);
      setLinkSelection(setLast12MonthsState, last12MonthsState, false);
      setLinkSelection(setEveryoneState, everyoneState, false);
      break;
    case TimeRange.Month_12:
      setLinkSelection(setLast30DaysState, last30DaysState, false);
      setLinkSelection(setLast6MonthsState, last6MonthsState, false);
      setLinkSelection(setLast12MonthsState, last12MonthsState, true);
      setLinkSelection(setEveryoneState, everyoneState, false);
      break;
    default:
      setLinkSelection(setLast30DaysState, last30DaysState, false);
      setLinkSelection(setLast6MonthsState, last6MonthsState, false);
      setLinkSelection(setLast12MonthsState, last12MonthsState, false);
      setLinkSelection(setEveryoneState, everyoneState, true);
      break;
  }
};
